import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UploadFileService } from '../../../../shared/services/file-upload.service';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DocumentTypesEnum } from '../../../../../assets/enum/documentTypeEnum';
import { ConfirmDialog } from '../../../../shared/components/confirm-dialog/confirm-dialog';
import { MatDialog } from '@angular/material/dialog';
import { CustomerService } from '../../../../features/customers/services/customer.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-customer-documents',
  templateUrl: './customer-documents.component.html',
  styleUrls: ['../../client-new.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    MatDatepickerModule,
  ],
})
export class CustomerDocumentsComponent {
  documentForm: FormGroup;
  @Input() customerData: any = {};
  @Output() formSubmit = new EventEmitter<any>();
  public documentTypeData: any[] = [];
  customerId: string | null = null;
  file: File | null = null;
  fileName: string | null = null;
  fileUrl: string | null = null;
  uploadedDocuments: any[] = [];
  uploadLoading = false;

  constructor(
    private fb: FormBuilder,
    private documentUploadService: UploadFileService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private customerService: CustomerService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.documentForm = this.fb.group({
      documents: this.fb.array([this.createDocumentGroup()]),
    });
    this.loadDocumentTypes();

    this.route.paramMap.subscribe((params) => {
      this.customerId = params.get('id');
      if (this.customerId) {
        this.fetchCustomerById();
      }
    });
  }

  get documentsArray(): FormArray {
    return this.documentForm.get('documents') as FormArray;
  }

  fetchCustomerById() {
    this.customerService.fetchCustomerById(Number(this.customerId)).subscribe({
      next: (data) => {
        this.customerData = data;
        this.populateFormWithCustomerData();
      },
      error: (error) => {
        console.error(error, 'Error');
      },
    });
  }

  populateFormWithCustomerData() {
    if (this.customerData?.customerDocs?.length > 0) {
      this.uploadedDocuments = this.customerData.customerDocs.map(
        (doc: any) => ({
          id: doc.id,
          documentType: doc.documentType,
          documentName: doc.documentName,
          uploadedFile: doc.uploadedFile,
          issueDate: doc.issueDate,
          expiryDate: doc.expiryDate,
          isActive: doc.isActive,
          status: 1,
        })
      );
    }
  }

  editUploadedDocument(index: number): void {
    const selectedDoc = this.uploadedDocuments[index];
    if (!selectedDoc) return;

    const formGroup = this.createDocumentGroup();
    formGroup.patchValue({
      id: selectedDoc.id,
      documentName: selectedDoc.documentType,
      issueDate: selectedDoc.issueDate,
      expiryDate: selectedDoc.expiryDate,
    });

    this.documentsArray.clear();
    this.documentsArray.push(formGroup);

    this.fileName = selectedDoc.documentName;
    this.file = null;
    this.fileUrl = selectedDoc.uploadedFile;

    this.uploadedDocuments.splice(index, 1); // remove from list temporarily
  }

  removeUploadedDocument(index: number, docId: number): void {
    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.uploadedDocuments.splice(index, 1);
        if (docId) {
          this.onDeleteDocument(docId);
        } else {
          this.toastr.success('Document removed');
        }
      }
    });
  }

  onDeleteDocument(id: any) {
    this.customerService.removeCustomerDocument(id).subscribe({
      next: (result) => {
        if (result) {
          this.toastr.success('Document removed.');
        }
      },
      error: (error) => {
        this.toastr.error(error, 'Error');
      },
    });
  }


  private createDocumentGroup(): FormGroup {
    return this.fb.group({
      id: [null], // Added to fix the patching error
      documentName: ['', Validators.required],
      file: ['', Validators.required],
      issueDate: [''],
      expiryDate: [''],
    });
  }

  formatDocumentLabel(key: string): string {
    return key
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private loadDocumentTypes() {
    this.documentTypeData = Object.values(DocumentTypesEnum).map((type) => ({
      label: type.label,
      value: type.value,
    }));
  }


  getMinExpiryDate(index: number): Date | null {
    const issueDate = this.documentsArray.at(index).get('issueDate')?.value;
    return issueDate ? new Date(issueDate) : null;
  }

  getMaxIssueDate(index: number): Date | null {
    const expiryDate = this.documentsArray.at(index).get('expiryDate')?.value;
    return expiryDate ? new Date(expiryDate) : null;
  }

  // onFileSelected(event: Event): void {
  //   const input = event.target as HTMLInputElement;
  //   if (input.files && input.files.length > 0) {
  //     this.file = input.files[0];
  //     this.fileName = this.file.name;
  //   }
  // }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const validTypes = [
        'application/pdf',
        'text/csv',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // .docx
      ];
      const maxSizeMB = 15;
  
      if (!validTypes.includes(file.type)) {
        this.toastr.warning('Only CSV, PDF, JPG, PNG, or DOC files are allowed');
        input.value = '';
        return;
      }
  
      if (file.size > maxSizeMB * 1024 * 1024) {
        this.toastr.warning('Please choose a file smaller than 15MB');
        input.value = '';
        return;
      }
  
      this.file = file;
      this.fileName = file.name;
    }
  }

  
  uploadAndSaveDocument(): Promise<{ url: string; documentName: string }> {
    return new Promise((resolve, reject) => {
      if (this.file) {
        this.uploadLoading = true;
        this.documentUploadService
          .uploadDocument(this.file, 'customers')
          .subscribe({
            next: (response) => {
              this.uploadLoading = false;
              resolve({ url: response.url, documentName: response.fileName });
            },
            error: (error) => {
              this.uploadLoading = false;
              this.toastr.error('Upload failed', 'Error');
              reject(error);
            },
          });
      } else {
        reject('No file');
      }
    });
  }

  handleAddDocument(index: number): void {
    const docGroup = this.documentsArray.at(index);

    if (!this.file && this.fileUrl) {
      docGroup.get('file')?.clearValidators();
      docGroup.get('file')?.updateValueAndValidity();
    }

    if (docGroup.invalid || (!this.file && !this.fileUrl)) {
      this.toastr.error(
        'Please choose a document type and upload a file.',
        'Validation Error'
      );
      return;
    }

    const handleUploadSuccess = (url: string, fileName: string) => {
      const id = docGroup.get('id')?.value;
      this.uploadedDocuments.push({
        ...(id && { id }), // Add id if present
        documentType: docGroup.get('documentName')?.value,
        documentName: fileName,
        uploadedFile: url,
        issueDate: docGroup.get('issueDate')?.value,
        expiryDate: docGroup.get('expiryDate')?.value,
        isActive: true,
        status: 1,
      });

      this.file = null;
      this.fileName = null;
      this.fileUrl = null;
      docGroup.reset();
      const input = document.querySelector(
        'input[type="file"]'
      ) as HTMLInputElement;
      if (input) input.value = '';
    };

    if (this.file) {
      this.uploadAndSaveDocument()
        .then((res) => handleUploadSuccess(res.url, res.documentName || ''))
        .catch(() => this.toastr.error('File upload failed', 'Error'));
    } else {
      handleUploadSuccess(this.fileUrl || '', this.fileName || '');
    }
  }

  removeFile(): void {
    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.file = null;
        this.fileName = null;
        this.fileUrl = null;
        const input = document.querySelector(
          'input[type="file"]'
        ) as HTMLInputElement;
        if (input) input.value = '';
        this.documentForm.patchValue({ file: null });
      }
    });
  }

  onSubmit(): void {
    // if (this.uploadedDocuments.length === 0) {
    //   this.toastr.error(
    //     'Please upload at least one document',
    //     'Validation Error'
    //   );
    //   return;
    // }

    const formatted = this.uploadedDocuments.map((doc) => ({
      ...(this.customerId && doc.id ? { id: doc.id } : {}),
      documentType: doc.documentType,
      documentName: doc.documentName,
      issueDate: doc.issueDate === '' ? null : doc.issueDate,
      expiryDate: doc.expiryDate === '' ? null : doc.expiryDate,
      isActive: true,
      uploadedFile: doc.uploadedFile || doc.documentUrl,
      status: 1,
    }));

    this.formSubmit.emit(formatted);
  }

  onCancel(): void {
    this.uploadedDocuments = [];

    this.file = null;
    this.fileName = null;
    this.fileUrl = null;
    this.documentsArray.clear();
    this.documentsArray.push(this.createDocumentGroup());
    const input = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (input) input.value = '';
  }
}
